import { useEffect, useId, useMemo, useRef, useState, type ButtonHTMLAttributes, type PropsWithChildren, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Check, ChevronDown, Search, X } from 'lucide-react';

export { PasswordField } from './PasswordField';

export function BrandMark() {
  return <div className="pb-brand" aria-label="PocketBuy"><img src="/pocketbuy-logo.png" alt=""/><strong>PocketBuy</strong></div>;
}

export function PageHeader({ title, eyebrow, action, onBack }: { title: string; eyebrow?: string; action?: ReactNode; onBack?: () => void }) {
  if (onBack) return <><header className="pb-detail-header">
    <div className={`pb-detail-bar${action?' pb-detail-bar-action':''}`}><button className="pb-icon-button" onClick={onBack} aria-label="Go back"><ArrowLeft size={22}/></button><h1>{title}</h1>{action?<div className="pb-page-action">{action}</div>:<span aria-hidden="true"/>}</div>
  </header>{eyebrow&&<p className="pb-detail-context">{eyebrow}</p>}</>;
  return <header className="pb-page-header">
    <div className="pb-page-heading">{eyebrow && <span>{eyebrow}</span>}<div className="pb-title-row"><h1>{title}</h1>{action && <div className="pb-page-action">{action}</div>}</div></div>
  </header>;
}

export function Button({ className = '', ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button className={`pb-button ${className}`.trim()} {...props} />;
}

export function Card({ children, className = '' }: PropsWithChildren<{ className?: string }>) {
  return <section className={`pb-card ${className}`.trim()}>{children}</section>;
}

export function AppFrame({ children, className = '' }: PropsWithChildren<{ className?: string }>) {
  return <main className={`pb-app-frame ${className}`.trim()}>{children}</main>;
}

type PhoneCountry = {
  countryCode: string;
  countryName: string;
  callingCode: string;
  nationalNumberLength: number;
};

function countryFlag(countryCode: string) {
  return countryCode.toUpperCase().replace(/[A-Z]/g, letter =>
    String.fromCodePoint(127397 + letter.charCodeAt(0)));
}

export function PhoneNumberField({ countries, countryCode, number, onCountryChange, onNumberChange }: {
  countries: PhoneCountry[];
  countryCode: string;
  number: string;
  onCountryChange: (countryCode: string) => void;
  onNumberChange: (number: string) => void;
}) {
  const hintId = useId();
  const country = countries.find(item => item.countryCode === countryCode);
  const requiredDigits = country?.nationalNumberLength ?? 0;
  const digits = number.replace(/\D/g, '').slice(0, requiredDigits || undefined);
  const hint = requiredDigits
    ? digits.length === 0 ? `${requiredDigits} digits required` : `${digits.length} of ${requiredDigits} digits`
    : 'Select a country code';

  return <label className="pb-field">Phone number
    <div className="pb-phone-input">
      <div className="pb-country-prefix">
        <span aria-hidden="true">{country ? countryFlag(country.countryCode) : '🌐'}</span>
        <strong>+{country?.callingCode ?? ''}</strong>
        <span className="pb-country-chevron" aria-hidden="true">⌄</span>
        <select aria-label="Country code" value={countryCode} onChange={event => onCountryChange(event.target.value)}>
          {countries.map(item => <option key={item.countryCode} value={item.countryCode}>
            {countryFlag(item.countryCode)} +{item.callingCode} · {item.countryName}
          </option>)}
        </select>
      </div>
      <input
        aria-label="Phone number"
        aria-describedby={hintId}
        value={digits}
        onChange={event => onNumberChange(event.target.value.replace(/\D/g, '').slice(0, requiredDigits || undefined))}
        inputMode="numeric"
        autoComplete="tel-national"
        maxLength={requiredDigits || undefined}
        placeholder={requiredDigits ? `Enter ${requiredDigits} digits` : 'Phone number'}
      />
    </div>
    <small id={hintId} className={`pb-field-hint ${digits.length > 0 && digits.length !== requiredDigits ? 'is-incomplete' : ''}`}>{hint}</small>
  </label>;
}

export type SelectOption = { code: string; label: string; description?: string };

export function SearchableSelect({ id, value, options, placeholder, title = 'Choose an option', searchPlaceholder = 'Search', alwaysShowSearch = false, onChange, invalid = false, disabled = false, filterContent, optionFilter, multiline = false }: {
  id: string;
  value: string;
  options: SelectOption[];
  placeholder: string;
  title?: string;
  searchPlaceholder?: string;
  alwaysShowSearch?: boolean;
  onChange: (value: string) => void;
  invalid?: boolean;
  disabled?: boolean;
  multiline?: boolean;
  filterContent?: ReactNode;
  optionFilter?: (option: SelectOption) => boolean;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = options.find(option => option.code === value);
  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    return options.filter(option => (!optionFilter || optionFilter(option)) && (!needle || `${option.label} ${option.description??''}`.toLocaleLowerCase().includes(needle)));
  }, [options, query, optionFilter]);
  const dismiss = () => { setOpen(false); setQuery(''); };

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node) && !dialogRef.current?.contains(event.target as Node)) dismiss();
    };
    document.addEventListener('pointerdown', closeOnOutsidePress);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePress);
  }, [open]);

  const showSearch = alwaysShowSearch || options.length > 8;

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => dialogRef.current?.focus({preventScroll:true}), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  const choose = (code: string) => { onChange(code); dismiss(); };
  return <div className={`pb-combobox ${open ? 'is-open' : ''}${multiline ? ' pb-combobox-multiline' : ''}`} ref={rootRef}>
    <button id={id} type="button" disabled={disabled} className="pb-combobox-trigger" aria-haspopup="listbox" aria-expanded={open} aria-invalid={invalid} onClick={() => open ? dismiss() : setOpen(true)}>
      <span className={selected ? '' : 'pb-combobox-placeholder'}>{selected?.label ?? placeholder}{multiline&&selected?.description&&<small>{selected.description}</small>}</span><ChevronDown size={18}/>
    </button>
    {open && createPortal(<div onClick={event => event.stopPropagation()}><button type="button" className="pb-sheet-backdrop" aria-label="Close options" onClick={dismiss}/><div ref={dialogRef} tabIndex={-1} className={`pb-combobox-popover${filterContent ? ' pb-combobox-filtered' : ''}${multiline ? ' pb-combobox-multiline' : ''}`} role="dialog" aria-modal="true" aria-label={title} onKeyDown={event => { if (event.key === 'Escape') dismiss(); }}>
      <div className="pb-sheet-header"><strong>{title}</strong><button type="button" aria-label="Close" onClick={dismiss}><X size={20}/></button></div>
      {showSearch && <div className="pb-combobox-search"><Search size={17}/><input ref={searchRef} id={`${id}-search`} name={`${id}-search`} type="search" inputMode="search" enterKeyHint="search" autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false} value={query} onChange={event => setQuery(event.target.value)} placeholder={searchPlaceholder} aria-label={searchPlaceholder} onKeyDown={event => { if (event.key === 'Escape') dismiss(); }}/></div>}
      {filterContent && <div className="pb-combobox-filters">{filterContent}</div>}
      <div className="pb-combobox-options" role="listbox" aria-labelledby={id}>
        {filtered.map(option => <button type="button" role="option" aria-selected={option.code === value} key={option.code} onClick={() => choose(option.code)}><span className="pb-combobox-option-copy"><span>{option.label}</span>{option.description && <small>{option.description}</small>}</span>{option.code === value && <Check size={17}/>}</button>)}
        {filtered.length === 0 && <p>No matching options</p>}
      </div>
    </div></div>, document.body)}
  </div>;
}
